import { useState, useEffect, useCallback } from 'react';

// Étapes de progression pour la démo
export type FileProgressionStep = 
    | 'INITIAL'           // Début - fichiers de base seulement
    | 'EXPLORER_OPENED'   // Explorateur ouvert - débloquer dossier formation
    | 'FILE_OPENED'       // Premier fichier ouvert - débloquer plus de fichiers
    | 'BOARD_OPENED'      // Tableau ouvert - débloquer images
    | 'FILES_DROPPED'     // Fichiers sur tableau - débloquer dossier avancé
    | 'LINK_CREATED'      // Lien créé - débloquer dossier admin
    | 'DEMO_COMPLETE';    // Démo terminée - tout débloqué

interface FileNotification {
    id: string;
    fileName: string;
    source: string;
    timestamp: number;
}

/**
 * Hook pour gérer la progression des fichiers dans la démo
 * Les fichiers se débloquent progressivement selon l'avancement du tutoriel
 */
export const useFileProgression = () => {
    const [step, setStep] = useState<FileProgressionStep>(() => {
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        if (saved) {
            try {
                const data = JSON.parse(saved);
                return data.fileProgressionStep || 'INITIAL';
            } catch (e) {}
        }
        return 'INITIAL';
    });

    const [unlockedFiles, setUnlockedFiles] = useState<Set<string>>(() => {
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        if (saved) {
            try {
                const data = JSON.parse(saved);
                if (data.unlockedFiles && data.unlockedFiles.length > 0) {
                    return new Set(data.unlockedFiles);
                }
            } catch (e) {}
        }
        // Fichiers de base débloqués au début
        return new Set([
            'root_demo',
            'root_sys',
            'sys_readme',
            'sys_version'
        ]);
    });

    const [notifications, setNotifications] = useState<FileNotification[]>([]);

    // Fonction pour débloquer des fichiers selon l'étape
    const unlockFilesForStep = useCallback((targetStep: FileProgressionStep) => {
        let newFiles: string[] = [];
        
        switch (targetStep) {
            case 'EXPLORER_OPENED':
                newFiles = ['folder_formation', 'f_manuel'];
                break;
            case 'FILE_OPENED':
                newFiles = ['f_guide_interface', 'f_procedures'];
                break;
            case 'BOARD_OPENED':
                newFiles = ['f_image_a', 'f_image_b'];
                break;
            case 'FILES_DROPPED':
                newFiles = ['folder_avance', 'f_analyse_optique'];
                break;
            case 'LINK_CREATED':
                newFiles = ['folder_admin'];
                break;
            case 'DEMO_COMPLETE':
                newFiles = ['f_certificat'];
                break;
        }

        if (newFiles.length > 0) {
            const actuallyNewFiles = newFiles.filter(fileId => !unlockedFiles.has(fileId));
            
            if (actuallyNewFiles.length > 0) {
                setUnlockedFiles(prev => {
                    const next = new Set(prev);
                    actuallyNewFiles.forEach(id => next.add(id));
                    return next;
                });

                // Créer les notifications
                const newNotifications = actuallyNewFiles.map(fileId => ({
                    id: `notif_${Date.now()}_${Math.random()}`,
                    fileName: fileId,
                    source: 'SYSTÈME',
                    timestamp: Date.now()
                }));

                setNotifications(prev => [...prev, ...newNotifications]);

                // Déclencher l'événement
                window.dispatchEvent(new CustomEvent('FILES_UNLOCKED', { 
                    detail: { fileIds: actuallyNewFiles, source: 'SYSTÈME' } 
                }));

                console.log('FileProgression - Fichiers débloqués pour', targetStep, ':', actuallyNewFiles);
            }
        }
    }, [unlockedFiles]);

    // Sauvegarde
    useEffect(() => {
        const saved = localStorage.getItem('ALBION_SAVE_DATA');
        let data = saved ? JSON.parse(saved) : {};
        data.unlockedFiles = Array.from(unlockedFiles);
        data.fileProgressionStep = step;
        localStorage.setItem('ALBION_SAVE_DATA', JSON.stringify(data));
    }, [unlockedFiles, step]);

    // Écouter les événements du tutoriel pour débloquer les fichiers
    useEffect(() => {
        const handleWindowOpened = (e: CustomEvent) => {
            const windowId = e.detail?.windowId;
            
            if (windowId === 'explorer' && step === 'INITIAL') {
                console.log('FileProgression - Explorateur ouvert, déblocage fichiers formation');
                setStep('EXPLORER_OPENED');
                unlockFilesForStep('EXPLORER_OPENED');
            } else if (windowId === 'board' && step === 'FILE_OPENED') {
                console.log('FileProgression - Tableau ouvert, déblocage images');
                setStep('BOARD_OPENED');
                unlockFilesForStep('BOARD_OPENED');
            }
        };

        const handleFileOpened = () => {
            if (step === 'EXPLORER_OPENED') {
                console.log('FileProgression - Fichier ouvert, déblocage fichiers supplémentaires');
                setStep('FILE_OPENED');
                unlockFilesForStep('FILE_OPENED');
            }
        };

        const handleFilesDropped = () => {
            if (step === 'BOARD_OPENED') {
                console.log('FileProgression - Fichiers droppés, déblocage dossier avancé');
                setStep('FILES_DROPPED');
                unlockFilesForStep('FILES_DROPPED');
            }
        };

        const handleLinkCreated = () => {
            if (step === 'FILES_DROPPED') {
                console.log('FileProgression - Lien créé, déblocage dossier admin');
                setStep('LINK_CREATED');
                unlockFilesForStep('LINK_CREATED');
            }
        };

        const handleEnhancerOpened = () => {
            if (step === 'LINK_CREATED') {
                console.log('FileProgression - Amélioration optique utilisée, déblocage certificat');
                setStep('DEMO_COMPLETE');
                unlockFilesForStep('DEMO_COMPLETE');
            }
        };

        // Écouter les notifications personnalisées (comme le fond d'écran de certification)
        const handleCustomNotification = (e: CustomEvent) => {
            const { fileName, source, id } = e.detail;
            
            const newNotification: FileNotification = {
                id: id || `notif_${Date.now()}_${Math.random()}`,
                fileName,
                source: source || 'SYSTÈME',
                timestamp: Date.now()
            };

            setNotifications(prev => [...prev, newNotification]);
            console.log('FileProgression - Notification personnalisée ajoutée:', fileName);
        };

        window.addEventListener('WINDOW_OPENED', handleWindowOpened as EventListener);
        window.addEventListener('FILE_OPENED', handleFileOpened as EventListener);
        window.addEventListener('BOARD_FILES_DROPPED', handleFilesDropped as EventListener);
        window.addEventListener('BOARD_LINK_CREATED', handleLinkCreated as EventListener);
        window.addEventListener('IMAGE_ENHANCER_OPENED', handleEnhancerOpened as EventListener);
        window.addEventListener('NEW_FILE_NOTIFICATION', handleCustomNotification as EventListener);

        return () => {
            window.removeEventListener('WINDOW_OPENED', handleWindowOpened as EventListener);
            window.removeEventListener('FILE_OPENED', handleFileOpened as EventListener);
            window.removeEventListener('BOARD_FILES_DROPPED', handleFilesDropped as EventListener);
            window.removeEventListener('BOARD_LINK_CREATED', handleLinkCreated as EventListener);
            window.removeEventListener('IMAGE_ENHANCER_OPENED', handleEnhancerOpened as EventListener);
            window.removeEventListener('NEW_FILE_NOTIFICATION', handleCustomNotification as EventListener);
        };
    }, [step, unlockFilesForStep]);

    const unlockFiles = useCallback((fileIds: string[], source: string = 'SYSTÈME') => {
        const newFiles: string[] = [];
        
        fileIds.forEach(fileId => {
            if (!unlockedFiles.has(fileId)) {
                newFiles.push(fileId);
            }
        });

        if (newFiles.length > 0) {
            setUnlockedFiles(prev => {
                const next = new Set(prev);
                newFiles.forEach(id => next.add(id));
                return next;
            });

            // Créer les notifications
            const newNotifications = newFiles.map(fileId => ({
                id: `notif_${Date.now()}_${Math.random()}`,
                fileName: fileId,
                source,
                timestamp: Date.now()
            }));

            setNotifications(prev => [...prev, ...newNotifications]);

            // Déclencher l'événement
            window.dispatchEvent(new CustomEvent('FILES_UNLOCKED', { 
                detail: { fileIds: newFiles, source } 
            }));
        }
    }, [unlockedFiles]);

    const advanceStep = useCallback((nextStep: FileProgressionStep) => {
        setStep(nextStep);
    }, []);

    const dismissNotification = useCallback((notifId: string) => {
        setNotifications(prev => prev.filter(n => n.id !== notifId));
    }, []);

    const isFileUnlocked = useCallback((fileId: string) => {
        return unlockedFiles.has(fileId);
    }, [unlockedFiles]);

    return {
        unlockedFiles: Array.from(unlockedFiles),
        notifications,
        step,
        advanceStep,
        unlockFiles,
        dismissNotification,
        isFileUnlocked
    };
};
